/**
 * useTheme — manages Night Mode / Track Mode toggle
 *
 * Reads from localStorage on first load to prevent flash.
 * Writes to document.documentElement data-theme attribute.
 * Exported as a hook so any component can toggle or read theme.
 */

import { useState, useEffect } from 'react';

const STORAGE_KEY = 'ss_theme';
const DEFAULT     = 'dark';

// Apply theme synchronously before React renders — prevents flash of wrong theme.
// Called once at module load time (not inside the hook).
const initialTheme = localStorage.getItem(STORAGE_KEY) || DEFAULT;
document.documentElement.setAttribute('data-theme', initialTheme);

export function useTheme() {
  const [theme, setTheme] = useState(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggle = () => setTheme(t => t === 'dark' ? 'light' : 'dark');
  const isDark  = theme === 'dark';
  const isLight = theme === 'light';

  return { theme, toggle, isDark, isLight };
}

export default useTheme;
